// Every key the bridge binds, with what it does in plain words. src/input.ts
// dispatches its keys from these rows and the key list draws them, so a key
// cannot be bound there without being listed.

import type { InputEvent } from './input';
import { TEST_SWITCHES } from './urlFlags';

export type KeyGroup = 'FLIGHT' | 'TIME' | 'CAMERA' | 'HELM' | 'TARGETING' | 'WEAPONS' | 'SYSTEM' | 'ATLAS' | 'DECISIONS' | 'MOUSE';

/** Drawing order of the groups in the key list. */
export const KEY_GROUPS: readonly KeyGroup[] = ['FLIGHT', 'TIME', 'CAMERA', 'HELM', 'TARGETING', 'WEAPONS', 'SYSTEM', 'ATLAS', 'DECISIONS', 'MOUSE'];

interface Listed {
  /** What the key cap reads. */
  cap: string;
  does: string;
  group: KeyGroup;
}

/** Read every frame while it is down. */
export interface HeldKey extends Listed {
  /** `KeyboardEvent.key`, lower-cased. */
  key: string;
  axis: 'throttle' | 'yaw' | 'pitch' | 'roll' | 'allStop';
  sign: 1 | -1;
}

/** Fires its events once, on the first keydown. */
export interface PressKey extends Listed {
  /** `KeyboardEvent.key`, lower-cased. */
  key: string;
  /** true: only with Shift held; false: only without; absent: either. */
  shift?: boolean;
  events: readonly InputEvent[];
  /** `noModal`: not while a true modal is up; `tabula`: `tabulaKey`'s own rule. */
  guard?: 'noModal' | 'tabula';
  /** Also answers while the Atlas has the frame. */
  inAtlas?: boolean;
}

/** Bound by its own listener outside src/input.ts, which matches it with `isKey`. */
export interface SystemKey extends Listed {
  id: 'menu' | 'editor' | 'restart';
  /** `KeyboardEvent.key`, exact case. */
  key: string;
  shift?: boolean;
}

/** A mouse gesture: listed, not dispatched from here. */
export interface PointerAction extends Listed {}

export const HELD_KEYS: readonly HeldKey[] = [
  { key: 'w', axis: 'throttle', sign: 1, cap: 'W', does: 'thrust forward', group: 'FLIGHT' },
  { key: 's', axis: 'throttle', sign: -1, cap: 'S', does: 'thrust back', group: 'FLIGHT' },
  { key: 'a', axis: 'yaw', sign: 1, cap: 'A', does: 'turn the nose left', group: 'FLIGHT' },
  { key: 'd', axis: 'yaw', sign: -1, cap: 'D', does: 'turn the nose right', group: 'FLIGHT' },
  { key: 'arrowleft', axis: 'yaw', sign: 1, cap: 'LEFT', does: 'turn the nose left', group: 'FLIGHT' },
  { key: 'arrowright', axis: 'yaw', sign: -1, cap: 'RIGHT', does: 'turn the nose right', group: 'FLIGHT' },
  { key: 'arrowup', axis: 'pitch', sign: 1, cap: 'UP', does: 'pitch the nose up', group: 'FLIGHT' },
  { key: 'arrowdown', axis: 'pitch', sign: -1, cap: 'DOWN', does: 'pitch the nose down', group: 'FLIGHT' },
  { key: 'q', axis: 'roll', sign: 1, cap: 'Q', does: 'roll left', group: 'FLIGHT' },
  { key: 'e', axis: 'roll', sign: -1, cap: 'E', does: 'roll right', group: 'FLIGHT' },
  { key: ' ', axis: 'allStop', sign: 1, cap: 'SPACE', does: 'all stop: throttle to zero while held', group: 'FLIGHT' },
];

export const PRESS_KEYS: readonly PressKey[] = [
  { key: '1', events: ['warpLevel1'], guard: 'noModal', cap: '1', does: 'time at real speed (REAL)', group: 'TIME' },
  { key: '2', events: ['warpLevel2'], guard: 'noModal', cap: '2', does: 'one minute a second (MIN)', group: 'TIME' },
  { key: '3', events: ['warpLevel3'], guard: 'noModal', cap: '3', does: 'one hour a second (HR)', group: 'TIME' },
  { key: '4', events: ['warpLevel4'], guard: 'noModal', cap: '4', does: 'one day a second (DAY)', group: 'TIME' },
  { key: '[', events: ['simSpeedDown'], cap: '[', does: 'time one step slower', group: 'TIME' },
  { key: ']', events: ['simSpeedUp'], cap: ']', does: 'time one step faster', group: 'TIME' },
  { key: 'g', events: ['cameraReset'], cap: 'G', does: 'reset the camera: behind the ship at its own distance, pin off (CHASE VIEW)', group: 'CAMERA' },
  { key: 'p', events: ['pin'], cap: 'P', does: 'hold the target, or the body you orbit, in the middle of the view (PIN)', group: 'CAMERA' },
  { key: 'n', events: ['names'], cap: 'N', does: 'names on the planets and the bodies near you (NAMES)', group: 'CAMERA' },
  { key: 'm', events: ['dvMap'], cap: 'M', does: 'open or fold the delta-v map', group: 'CAMERA' },
  { key: 'l', events: ['land'], cap: 'L', does: 'land: open the Atlas at the body with the largest pull on you (LAND)', group: 'HELM' },
  { key: 'o', events: ['launch'], inAtlas: true, cap: 'O', does: 'launch back to orbit, while resting on a surface', group: 'HELM' },
  { key: 'h', events: ['het'], cap: 'H', does: 'high-energy turn: snap the ship round 180 degrees (HET)', group: 'HELM' },
  { key: 't', events: ['radiators'], cap: 'T', does: 'stow or deploy the radiators', group: 'HELM' },
  { key: 'b', events: ['redLine'], cap: 'B', does: 'push the drive past the heat red line, or ease back', group: 'HELM' },
  { key: 'r', shift: false, events: ['redAlert'], cap: 'R', does: 'red alert on or off (ALERT)', group: 'HELM' },
  { key: 'v', events: ['tabula'], guard: 'tabula', cap: 'V', does: 'call the officers to speak in turn (OFFICERS)', group: 'HELM' },
  { key: 'y', shift: false, events: ['cycleNext'], cap: 'Y', does: 'lock the next target', group: 'TARGETING' },
  { key: 'y', shift: true, events: ['cyclePrev'], cap: 'SHIFT+Y', does: 'lock the previous target', group: 'TARGETING' },
  { key: '\\', events: ['clearTarget'], cap: '\\', does: 'clear the target lock', group: 'TARGETING' },
  { key: 'z', events: ['fire'], cap: 'Z', does: 'fire a plasma torpedo at the locked target (PLASMA)', group: 'WEAPONS' },
  { key: 'x', events: ['firePhaser'], cap: 'X', does: 'fire the phaser beam, in a fight', group: 'WEAPONS' },
  { key: 'f', events: ['fireLance'], cap: 'F', does: 'fire the spinal Lance at the locked target, in a fight with ?armament=canon (1.1 s charge)', group: 'WEAPONS' },
  { key: 'c', events: ['firePulse', 'chaseView'], cap: 'C', does: 'fire a pulse bolt in a fight; in cruise, put the camera back behind the nose, keeping the zoom (CHASE)', group: 'WEAPONS' },
  { key: '?', events: ['keys'], cap: '?', does: 'this list of keys', group: 'SYSTEM' },
];

export const SYSTEM_KEYS: readonly SystemKey[] = [
  { id: 'menu', key: 'Escape', cap: 'ESC', does: 'main menu, or close this list', group: 'SYSTEM' },
  { id: 'editor', key: '`', cap: '`', does: 'game editor', group: 'SYSTEM' },
  { id: 'restart', key: 'R', shift: true, cap: 'SHIFT+R', does: 'restart the scenario', group: 'SYSTEM' },
];

/** Keys bound by their own listeners outside src/input.ts (src/atlas/input.ts, src/menu.ts, src/decisionPanel.ts): listed, not dispatched from here. */
export const ELSEWHERE_KEYS: readonly Listed[] = [
  { cap: '= -', does: 'camera in or out, while held (close in to the hull)', group: 'CAMERA' },
  { cap: '\\', does: 'in the menu: fold or unfold the tab rail', group: 'SYSTEM' },
  { cap: 'H', does: 'in the Atlas: show or hide the hex grid', group: 'ATLAS' },
  { cap: 'W A S D', does: 'in the Atlas, close to the ground: walk', group: 'ATLAS' },
  { cap: 'R F', does: 'in the Atlas, close to the ground: rise and sink', group: 'ATLAS' },
  { cap: 'SHIFT', does: 'in the Atlas, close to the ground: walk three times faster', group: 'ATLAS' },
  { cap: 'ESC', does: 'in the Atlas: drop the selection, then step out one level', group: 'ATLAS' },
  { cap: '1-9', does: 'in a fight, with a decision on screen: choose that option', group: 'DECISIONS' },
];

export const POINTER_ACTIONS: readonly PointerAction[] = [
  { cap: 'CLICK', does: 'on the view: set the heading; on a ship, rock or planet: lock it', group: 'MOUSE' },
  { cap: 'CLICK PIP', does: 'on the radar: lock that contact', group: 'MOUSE' },
  { cap: 'RIGHT DRAG', does: 'aim the nose; held off centre it keeps turning', group: 'MOUSE' },
  { cap: 'LEFT DRAG', does: 'look around the ship', group: 'MOUSE' },
  { cap: 'WHEEL', does: 'camera in or out', group: 'MOUSE' },
  { cap: 'THROTTLE', does: 'click or drag the throttle bar to hold a thrust level; W and S override it', group: 'MOUSE' },
  { cap: 'SHIFT+CLICK', does: 'on the throttle bar: snap to the nearest tick', group: 'MOUSE' },
];

/** The press row a keydown fires, if any. `k` is `e.key` lower-cased. */
export function pressKeyFor(k: string, shift: boolean): PressKey | undefined {
  return PRESS_KEYS.find((b) => b.key === k && (b.shift === undefined || b.shift === shift));
}

/** Does this keydown match the system key `id`? */
export function isKey(e: Pick<KeyboardEvent, 'key' | 'shiftKey'>, id: SystemKey['id']): boolean {
  const b = SYSTEM_KEYS.find((s) => s.id === id);
  return !!b && e.key === b.key && (b.shift === undefined || b.shift === e.shiftKey);
}

/** Every listed row in drawing order, grouped. */
export function keyList(): { group: KeyGroup; rows: Listed[] }[] {
  const system = TEST_SWITCHES ? SYSTEM_KEYS : SYSTEM_KEYS.filter((s) => s.id !== 'editor');
  const all: Listed[] = [...HELD_KEYS, ...PRESS_KEYS, ...system, ...ELSEWHERE_KEYS, ...POINTER_ACTIONS];
  return KEY_GROUPS.map((group) => ({ group, rows: all.filter((r) => r.group === group) }));
}
